import { useState } from 'react'
import Big from 'big.js'

// Tracking for the monthly-upsell panel — the nudge that asks a one-time donor
// to convert to a smaller recurring gift (see proposeMonthlyUpsell.js).
//
// State here IS the impression: `show()` is called when the panel opens whether
// or not the donor goes on to accept, because an absent record and a declined
// one have to be distinguishable for the report to compute a conversion rate
// rather than a bare count of conversions. It rides every saveDraft as
// monthly_upsell_attributes and Order uses update_only, so repeated draft saves
// update one row instead of accumulating rows.
//
// Nothing here is ever revoked. An earlier revision cleared the acceptance in
// an effect whenever the frequency left monthly, which silently reclassified a
// donor who accepted and then switched to annually as a decline — and did so
// irreversibly, since the panel only shows for one_time and never re-recorded.
// Instead the acceptance latches and the donor's *current* state is read fresh
// on every save, so the last save before payment is authoritative and the
// report can tell "accepted and held" from "accepted, then changed their mind"
// from "never accepted". See MonthlyUpsell for the resulting categories.
//
// Because there's no reset to sequence, there's also no ordering hazard between
// accept() and setSubscription() — the two are independent.
//
// Deliberately its own hook rather than cases in useDonation's reducer: this is
// reporting state that happens to read donation state, and the reducer is
// already the widget's most central state machine. Follows the same shape as
// useTribute / useCompanyContribution — own state, one draftOrderProps().
const useMonthlyUpsell = ({ subscription, currency, exponent, amount }) => {
  // null until the panel has been shown; { originalAmount, proposedAmount,
  // currency, accepted } after.
  const [ state, setState ] = useState(null)

  // Everything this hook records is normalised to the currency's scale, so the
  // three amounts are one measure and comparable in SQL. proposeMonthlyUpsell
  // ends in Math.min(proposed, oneTime) and so can hand back the donor's raw
  // one-time; on a zero-exponent currency (JPY, KRW, CLP, … — 14 supported
  // ones) that would be stored fractional and then compared against a rounded
  // final_amount, missing held_monthly for a donor who changed nothing.
  //
  // Rounding an offer amount down to zero would trip the model's greater_than:
  // 0 — the one way this record could newly fail a donation save, since the
  // unscaled string couldn't. It needs a sub-unit gift on a zero-exponent
  // currency, and Start.jsx enforces min={donation.minAmount} on the field, so
  // it can't reach handleStartContinue. Worth knowing if that input ever stops
  // being the only way in.
  const scaled = value => Big(value || 0).toFixed(exponent)

  // Re-showing after the donor goes back and edits overwrites the amounts: one
  // row per order means the offer that mattered is the last one before payment.
  // `accepted` carries over — a donor who accepted, went back, and was shown a
  // fresh offer has still accepted one.
  const show = ({ originalAmount, proposedAmount }) =>
    setState(current => ({
      originalAmount: scaled(originalAmount),
      proposedAmount: scaled(proposedAmount),
      currency,
      accepted: current?.accepted || false,
    }))

  // Guarded on existing state: an accept without a preceding show would be a
  // bug, and inventing a record with no amounts would fail the model's
  // validations mid-checkout.
  const accept = () =>
    setState(current => (current ? { ...current, accepted: true } : current))

  // Read at save time, not stored: final_* is wherever the donor is right now,
  // so the last save before payment wins without anything having to watch for
  // changes. `amount` is the gift alone (donation.giftAmount) — no tip, no
  // covered fees — which is what makes it comparable to proposed_amount.
  //
  // final_currency is the live currency while state.currency is the one the
  // offer was shown in. They diverge when a donor accepts, goes back to Start
  // and changes denomination: the panel needs one_time to re-show, so it can't
  // re-sync. Sending both lets the report separate a donor who changed their
  // gift from one who only changed its units.
  const draftOrderProps = () => {
    if (!state) return {}

    return {
      monthly_upsell_attributes: {
        original_amount: state.originalAmount,
        proposed_amount: state.proposedAmount,
        currency:        state.currency,
        accepted:        state.accepted,
        final_frequency: subscription,
        final_currency:  currency,
        final_amount:    scaled(amount),
      },
    }
  }

  return { state, accepted: !!state?.accepted, show, accept, draftOrderProps }
}

export default useMonthlyUpsell
